<template>
  <Popover
    ref="popover"
    trigger="click"
    :popover-class="popoverClass"
    :hide-trigger="true"
    placement="bottom"
    :align="align"
    :bound-to="boundToSelector ? { x: 'container' } : undefined"
    :bound-to-selector="boundToSelector"
    @show="onPopoverShown"
    @close="onPopoverClosed"
  >
    <template #content>
      <p
        v-if="notice"
        class="emoji-picker-notice"
      >
        <FAIcon icon="circle-info" />
        {{ notice }}
      </p>
      <div
        v-if="showingStrip"
        class="emoji-quick-box"
        :style="{ '--_cols': quickColumns }"
      >
        <label class="emoji-quick-search">
          <FAIcon icon="magnifying-glass" />
          <input
            ref="quickSearch"
            v-model="stripKeyword"
            v-live
            type="text"
            :placeholder="$t('emoji.search_emoji')"
            :aria-label="$t('emoji.search_emoji')"
            @input="quickActive = 0"
            @keydown="onQuickKey"
          >
        </label>
        <div
          class="emoji-quick"
          :class="searching ? 'emoji-quick-results' : 'emoji-quick-recent'"
          role="toolbar"
          :aria-label="searching ? $t('emoji.search_emoji') : $t('emoji.recent_emoji')"
        >
          <button
            v-for="(emoji, index) in quickItems"
            :key="emojiKey(emoji)"
            class="button-unstyled emoji-quick-item"
            :class="{ '-active': searching && index === quickActive }"
            type="button"
            :title="maybeLocalizedEmojiName(emoji)"
            @click.stop.prevent="onEmoji(emoji)"
          >
            <UnicodeEmoji
              v-if="!emoji.imageUrl"
              class="emoji-picker-emoji -unicode"
              :emoji="emoji.replacement"
            />
            <StillImage
              v-else
              class="emoji-picker-emoji -custom"
              :alt="maybeLocalizedEmojiName(emoji)"
              :src="emoji.imageUrl"
            />
          </button>
          <span
            v-if="searching && !quickItems.length"
            class="emoji-quick-none"
          >{{ $t('emoji.no_results') }}</span>
          <button
            class="button-unstyled emoji-quick-more"
            type="button"
            :title="$t('emoji.more_emoji')"
            :aria-label="$t('emoji.more_emoji')"
            @click.stop.prevent="expand"
          >
            <FAIcon icon="ellipsis-h" />
          </button>
        </div>
      </div>
      <template v-else>
        <div
          class="heading"
        >
          <div class="emoji-search">
            <input
              ref="search"
              v-model="keyword"
              v-live
              type="text"
              class="input form-control"
              :placeholder="$t('emoji.search_emoji')"
            >
          </div>
          <span
            ref="header"
            class="emoji-tabs"
            @wheel.prevent="groupScroll"
          >
            <span
              v-for="group in filteredEmojiGroups"
              :ref="setGroupRef('group-header-' + group.id)"
              :key="group.id"
              class="button-unstyled emoji-tabs-item"
              :class="{
                toggled: activeGroupView === group.id
              }"
              :title="group.text"
              role="button"
              @click.prevent="highlight(group.id)"
            >
              <span
                v-if="group.image"
                class="emoji-picker-header-image"
              >
                <StillImage
                  :alt="group.text"
                  :src="group.image"
                />
              </span>
              <FAIcon
                v-else
                :icon="group.icon"
                fixed-width
              />
            </span>
          </span>
          <span
            v-if="stickerPickerEnabled"
            class="additional-tabs"
          >
            <button
              type="button"
              class="button-unstyled stickers-tab-icon additional-tabs-item"
              :class="{toggled: showingStickers}"
              :title="$t('emoji.stickers')"
              :aria-pressed="showingStickers ? 'true' : 'false'"
              @click.prevent="toggleStickers"
            >
              <FAIcon
                icon="sticky-note"
                fixed-width
              />
            </button>
          </span>
        </div>
        <div
          v-if="contentLoaded"
          class="content"
        >
          <div
            class="emoji-content"
            :class="{hidden: showingStickers}"
          >
            <!-- Enables scrolling for this element on safari iOS. See comments for header. -->
            <component
              :is="scroller.DynamicScroller"
              v-if="scroller"
              ref="emoji-groups"
              class="emoji-groups"
              :class="groupsScrolledClass"
              :min-item-size="minItemSize"
              :buffer="minItemSize"
              :items="emojiItems"
              :emit-update="true"
              @update="onScroll"
              @visible="recalculateItemPerRow"
              @resize="recalculateItemPerRow"
            >
              <template #default="{ item: group, index, active }">
                <component
                  :is="scroller.DynamicScrollerItem"
                  :ref="setGroupRef('group-' + group.id)"
                  :item="group"
                  :active="active"
                  :data-index="index"
                  :size-dependencies="[group.emojis.length]"
                >
                  <div
                    class="emoji-group"
                    :class="{ ['first-row']: group.isFirstRow }"
                    :style="{ '--__amount': itemPerRow }"
                  >
                    <h6
                      v-if="group.isFirstRow"
                      class="emoji-group-title"
                    >
                      {{ group.text }}
                    </h6>
                    <span
                      v-for="emoji in group.emojis"
                      :key="group.id + emoji.displayText"
                      :title="maybeLocalizedEmojiName(emoji)"
                      class="emoji-item"
                      role="button"
                      @click.stop.prevent="onEmoji(emoji)"
                    >
                      <UnicodeEmoji
                        v-if="!emoji.imageUrl"
                        class="emoji-picker-emoji -unicode"
                        :emoji="emoji.replacement"
                      />
                      <StillImage
                        v-else
                        class="emoji-picker-emoji -custom"
                        loading="lazy"
                        :alt="maybeLocalizedEmojiName(emoji)"
                        :src="emoji.imageUrl"
                        :data-emoji-name="group.id + emoji.displayText"
                      />
                    </span>
                  </div>
                </component>
              </template>
            </component>
            <div class="keep-open">
              <Checkbox v-model="keepOpen">
                {{ $t('emoji.keep_open') }}
              </Checkbox>
            </div>
            <div
              v-if="!hideCustomEmoji"
              class="hide-custom-emoji"
            >
              <Checkbox
                v-model="hideCustomEmojiInPicker"
                @change="onShowing"
              >
                {{ $t('emoji.hide_custom_emoji') }}
              </Checkbox>
            </div>
          </div>
          <div
            v-if="showingStickers"
            class="stickers-content"
          >
            <sticker-picker
              @uploaded="onStickerUploaded"
              @upload-failed="onStickerUploadFailed"
            />
          </div>
        </div>
      </template>
    </template>
  </Popover>
</template>

<script src="./emoji_picker.js"></script>
<style lang="scss" src="./emoji_picker.scss"></style>
